/**
 * Katalog App Dashboard Layout — thin wrapper over AppDashboardShell.
 * Only katalog-specific config lives here; shell orchestration is in packages/ui.
 */
import React, { lazy, Suspense, useCallback, useState, useEffect } from 'react';
import {
  AgentCPanelContent,
  AppDashboardShell,
  type NavbarItemConfig,
  cn,
} from '@bright-web-skeleton/ui';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@bright-web-skeleton/supabase';
import { useAuth } from '@bright-web-skeleton/auth';
import {
  Home,
  Compass,
  PlusCircle,
  Bell,
  User,
  Heart,
  ShoppingCart,
  Archive,
  Shield,
  SlidersHorizontal,
  LogOut,
  UserCircle,
  ClipboardList,
  MapPin,
} from 'lucide-react';
import { routes } from '../../routes';
import { useGuestRegion } from '../../hooks/useGuestRegion';

/** "chris@example.com" → "Chris" */
function humanizeEmail(email?: string | null): string {
  if (!email) return '';
  const local = String(email).split('@')[0] || '';
  const cleaned = local.replace(/[._-]+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ');
}

/** ISO country code → emoji flag */
function countryFlag(code?: string | null): string {
  if (!code || code.length !== 2) return '';
  const cc = code.toUpperCase();
  return String.fromCodePoint(cc.charCodeAt(0) + 127397, cc.charCodeAt(1) + 127397);
}

// Lazy-load discover panel slot (only used on /discover)
const DiscoverRightPanelSlotLazy = lazy(() =>
  import('../discover/desktop/DiscoverRightPanelSlot').then((m) => ({
    default: m.DiscoverRightPanelSlot,
  })),
);

// ─── Nav items ───────────────────────────────────────────────────
const navbarItems: NavbarItemConfig[] = [
  { label: 'Home', path: routes.app.home, icon: Home },
  { label: 'Discover', path: routes.app.discover, icon: Compass },
  { label: 'Create', path: '/create', icon: PlusCircle },
  { label: 'Notifications', path: routes.app.notifications, icon: Bell },
  { label: 'Account', icon: User, action: 'toggle-sidebar' },
];

// ─── Sidebar items ───────────────────────────────────────────────
interface SidebarEntry {
  type: 'item' | 'headline';
  label: string;
  route?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

const sidebarItems: SidebarEntry[] = [
  { type: 'headline', label: 'COLLECTIONS' },
  { type: 'item', label: 'Cart', route: routes.app.cart, icon: ShoppingCart },
  { type: 'item', label: 'Wishlist', route: routes.app.wishlist, icon: Heart },
  { type: 'item', label: 'Orders', route: '/account/orders', icon: ClipboardList },
  { type: 'item', label: 'Stash', route: routes.app.stash, icon: Archive },
  { type: 'headline', label: 'ACCOUNT' },
  { type: 'item', label: 'Profile', route: routes.account.profile, icon: UserCircle },
  { type: 'item', label: 'Addresses', route: routes.account.addresses, icon: MapPin },
  { type: 'item', label: 'Security', route: routes.account.security, icon: Shield },
  {
    type: 'item',
    label: 'Preferences',
    route: routes.account.preferences,
    icon: SlidersHorizontal,
  },
];

// ─── Account sidebar content ─────────────────────────────────────
const AccountContent: React.FC<{
  regionLabel: string;
  onLinkClick: (path: string) => void;
  isPathActive: (path: string) => boolean;
  onSignOut: () => void;
}> = ({ regionLabel, onLinkClick, isPathActive, onSignOut }) => (
  <>
    {regionLabel && (
      <div className="px-4 pt-4 pb-2">
        <span className="text-xs text-muted-foreground">{regionLabel}</span>
      </div>
    )}
    <nav className="py-4 px-3" aria-label="Sidebar navigation">
      <ul className="space-y-1" role="list">
        {sidebarItems.map((entry, index) => {
          if (entry.type === 'headline') {
            return (
              <li key={`headline-${index}`} className="pt-4 first:pt-0">
                <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground select-none">
                  {entry.label}
                </h3>
              </li>
            );
          }
          const Icon = entry.icon;
          const active = entry.route ? isPathActive(entry.route) : false;
          return (
            <li key={entry.route || `item-${index}`}>
              <button
                onClick={() => entry.route && onLinkClick(entry.route)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left',
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}
                aria-current={active ? 'page' : undefined}
              >
                {Icon && <Icon className="w-5 h-5 shrink-0" />}
                {entry.label}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
    <div className="py-4 px-3 border-t border-border mt-auto">
      <button
        onClick={onSignOut}
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
      >
        <LogOut className="w-5 h-5 shrink-0" />
        Sign Out
      </button>
    </div>
  </>
);

// ─── Main export ─────────────────────────────────────────────────
export const DashboardLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { appProfile, authUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const guestRegion = useGuestRegion();
  const [agentCAccessToken, setAgentCAccessToken] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    void supabase.auth.getSession().then(({ data }) => {
      if (mounted) {
        setAgentCAccessToken(data.session?.access_token ?? null);
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) {
        setAgentCAccessToken(session?.access_token ?? null);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const isDiscoverRoute =
    location.pathname === '/discover' || location.pathname.startsWith('/discover/');

  // Manage Katalag is an ordinary long-form editor. Let the document own
  // vertical scrolling on this route instead of nesting it inside AppMain.
  const isKatalogManageRoute =
    /^\/account\/katalogs\/[^/]+\/manage\/?$/.test(location.pathname) ||
    /^\/account\/katalog\/[^/]+\/manage\/?$/.test(location.pathname);
  const viewportMode: 'page' | 'app' = isKatalogManageRoute ? 'page' : 'app';

  const isPathActive = useCallback(
    (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  const handleSignOut = useCallback(async () => {
    await supabase.auth.signOut();
    navigate('/auth');
  }, [navigate]);

  const handleSidebarLinkClick = useCallback(
    (path: string) => {
      navigate(path);
      // On mobile close the panel after navigation
      if (window.innerWidth < 768) {
        // setRightPanel is managed by AppDashboardShell via controlled mode
      }
    },
    [navigate],
  );

  const customerProfile = appProfile as {
    display_name?: string;
    username?: string;
    full_name?: string;
  } | null;
  const meta = (authUser?.user_metadata ?? {}) as {
    full_name?: string;
    name?: string;
    given_name?: string;
    first_name?: string;
  };
  const displayName =
    customerProfile?.display_name ||
    customerProfile?.full_name ||
    meta.full_name ||
    meta.name ||
    meta.given_name ||
    meta.first_name ||
    customerProfile?.username ||
    humanizeEmail(authUser?.email) ||
    'Customer';

  const flag = countryFlag(guestRegion.countryCode);
  const regionLabel = guestRegion.countryCode
    ? `${flag} Browsing from ${guestRegion.countryCode}`
    : guestRegion.loading
      ? ''
      : 'Choose delivery country';

  // ─── Logo ─────────────────────────────────────────────────────
  const logo = (
    <Link to={routes.app.home} className="flex items-center gap-2 no-underline">
      <img
        src="/brand/katalag-logo.png"
        alt="Katalag"
        className="h-8 w-8 rounded-lg object-contain"
      />
      <span className="text-lg font-semibold text-foreground hidden sm:inline">Katalag</span>
    </Link>
  );

  // ─── Discover panel slots ─────────────────────────────────────
  const discoverFilters = isDiscoverRoute ? (
    <Suspense fallback={null}>
      <DiscoverRightPanelSlotLazy panel="filters" />
    </Suspense>
  ) : undefined;

  const discoverSearch = isDiscoverRoute ? (
    <Suspense fallback={null}>
      <DiscoverRightPanelSlotLazy panel="search" />
    </Suspense>
  ) : undefined;

  const discoverAgentC = isDiscoverRoute ? (
    <AgentCPanelContent
      role="customer"
      supabaseUrl={import.meta.env.VITE_SUPABASE_URL ?? ''}
      accessToken={agentCAccessToken}
      context={{ type: 'discover', label: 'Discover' }}
      suggestedPrompts={[
        'Help me find deals I might like.',
        'Show me interesting BrandStores to explore.',
        'Help me compare what is available here.',
      ]}
    />
  ) : undefined;
  return (
    <AppDashboardShell
      logo={logo}
      navItems={navbarItems}
      onNavigate={navigate}
      isPathActive={isPathActive}
      viewportMode={viewportMode}
      mainClassName="bg-katalog-surface"
      rightPanelTitle={displayName}
      accountContent={
        <AccountContent
          regionLabel={regionLabel}
          onLinkClick={handleSidebarLinkClick}
          isPathActive={isPathActive}
          onSignOut={handleSignOut}
        />
      }
      filtersContent={discoverFilters}
      searchContent={discoverSearch}
      agentCContent={discoverAgentC}
    >
      {children}
    </AppDashboardShell>
  );
};
